import { useEffect, useRef, useState } from 'react';

import { Link, NavLink, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

/* Context */
import { useAuthContext } from '../../context/AuthContext';

/* Hooks */
import { useMediaQuery } from '../../hooks/useMediaQuery';

/* CSS */
import styles from './Header.module.css';

/* Images */
import travelbuddyLogo from '../../assets/icons/travelbuddy_logo.svg';

/* Icons */
import chevron_down from '../../assets/icons/chevron_down.svg';

import profile_icon from '../../assets/icons/profile_icon.svg';
import help_icon from '../../assets/icons/help_icon.svg';
import logout_icon from '../../assets/icons/logout_icon.svg';

const Header = () => {

    const navigate = useNavigate();

    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const profileButtonRef = useRef(null);
    const headerRef = useRef(null);

    // The horizontal nav collapses into a hamburger dropdown at/below 768px
    const isMobileLayout = useMediaQuery('(max-width: 768px)');

    // Grab the information out of AuthProvider
    const { currentUser, logout } = useAuthContext();

    // Auto-close the mobile nav dropdown if the window grows back to desktop width
    useEffect(() => {
        if (!isMobileLayout) setIsMobileNavOpen(false);
    }, [isMobileLayout]);

    // Close the mobile nav when tapping/clicking anywhere outside the header
    useEffect(() => {
        if (!isMobileNavOpen) return;
        const handleClickOutside = (event) => {
            if (headerRef.current && !headerRef.current.contains(event.target)) {
                setIsMobileNavOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isMobileNavOpen]);

    // Close menu on scroll or resize
    useEffect(() => {
        if (!isProfileMenuOpen) return;
        const handleScroll = () => setIsProfileMenuOpen(false);
        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll, true)
            window.removeEventListener('resize', handleScroll);
        };
    }, [isProfileMenuOpen]);

    // Close menu when clicking outside
    useEffect(() => {
        if (!isProfileMenuOpen) return;
        const handleClickOutside = (event) => {
            if (profileButtonRef.current &&
                !profileButtonRef.current.contains(event.target) &&
                !event.target.closest(`.${styles.profileMenu}`)
            ) {
                setIsProfileMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isProfileMenuOpen]);

    // Render the profile menu in a portal
    const renderMenu = () => {
        if (!isProfileMenuOpen) return null;

        return createPortal(
            <div
                className={styles.profileMenu}
                style={menuStyle}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className={styles.profileMenuItem}
                    onClick={() => handleNavigation('/settings')}
                >
                    <img src={profile_icon} alt='My Profile' className={styles.profileMenuIcon} />
                    <div>My Profile</div>
                </div>

                <div
                    className={styles.profileMenuItem}
                    onClick={() => handleNavigation('/help')}
                >
                    <img src={help_icon} alt='Help & Support' className={styles.profileMenuIcon} />
                    <div>Help & Support</div>
                </div>

                <div
                    className={styles.profileMenuItem}
                    onClick={handleLogout}
                >
                    <img src={logout_icon} alt='Log Out' className={styles.profileMenuIcon} />
                    <div>Log Out</div>
                </div>
            </div>,
            document.body
        )
    };

    // Toggle profile menu on/off
    const toggleMenu = (e) => {
        e.stopPropagation();
        setIsMobileNavOpen(false);

        if (isProfileMenuOpen) {
            setIsProfileMenuOpen(false);
            return;
        }

        if (profileButtonRef.current) {
            const rect = profileButtonRef.current.getBoundingClientRect();
            const menuWidth = 200; // Wider than trip menu

            setMenuStyle({
                position: 'absolute',
                top: `${rect.bottom + window.scrollY + 12}px`, // Slight gap below header
                left: `${rect.right + window.scrollX - menuWidth}px`, // Align right edge
                zIndex: 99999
            });
            setIsProfileMenuOpen(true);
        }
    };

    // Toggle the mobile nav dropdown on/off
    const toggleMobileNav = (e) => {
        e.stopPropagation();
        setIsProfileMenuOpen(false);
        setIsMobileNavOpen((open) => !open);
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    }

    const handleNavigation = (path) => {
        setIsProfileMenuOpen(false);
        navigate(path);
    }

    return (
        <>
            <header className={styles.appHeader} ref={headerRef}>
                <div className={styles.headerLeft}>
                    {!isMobileLayout &&
                        <Link to="/" className={styles.logoLink}>
                            <div className={styles.logoBackground}></div>
                            <img
                                src={travelbuddyLogo}
                                alt="TravelBuddy Logo"
                                className={styles.logoButton}
                            />
                        </Link>
                    }

                    <button
                        className={`${styles.hamburgerButton}${isMobileNavOpen ? ` ${styles.open}` : ''}`}
                        onClick={toggleMobileNav}
                        aria-label="Toggle navigation menu"
                        aria-expanded={isMobileNavOpen}
                    >
                        <span className={styles.hamburgerBar}></span>
                        <span className={styles.hamburgerBar}></span>
                        <span className={styles.hamburgerBar}></span>
                    </button>
                </div>

                <div className={styles.headerRight}>
                    <nav
                        className={`${styles.headerNav}${isMobileNavOpen ? ` ${styles.headerNavOpen}` : ''}`}
                        onClick={() => setIsMobileNavOpen(false)}
                    >
                        <NavLink
                            to="/"
                            className={({ isActive }) => styles.navLink + (isActive ? ` ${styles.navLinkActive}` : "")}>Home</NavLink>
                        <NavLink
                            to="/trips"
                            className={({ isActive }) => styles.navLink + (isActive ? ` ${styles.navLinkActive}` : "")}>My Trips</NavLink>
                        <NavLink
                            to="/summary"
                            className={({ isActive }) => styles.navLink + (isActive ? ` ${styles.navLinkActive}` : "")}>Trips Summary</NavLink>
                        <NavLink
                            to="/about"
                            className={({ isActive }) => styles.navLink + (isActive ? ` ${styles.navLinkActive}` : "")}>About</NavLink>
                        <NavLink
                            to="/help"
                            className={({ isActive }) => styles.navLink + (isActive ? ` ${styles.navLinkActive}` : "")}>Help</NavLink>
                        <NavLink
                            to="/settings"
                            className={({ isActive }) => styles.navLink + (isActive ? ` ${styles.navLinkActive}` : "")}>Settings</NavLink>
                    </nav>

                    <button
                        className={styles.profileButton}
                        ref={profileButtonRef}
                        onClick={toggleMenu}
                    >
                        <img
                            src={currentUser?.profile_picture_url}
                            alt="Profile Icon"
                            className={styles.profileIcon}
                            referrerPolicy="no-referrer"
                        />
                        <img
                            src={chevron_down}
                            alt="Toggle"
                            className={`${styles.headerChevron} ${isProfileMenuOpen ? styles.rotate : ''}`}
                        />
                    </button>
                </div>
            </header>

            {renderMenu()}
        </>
    );
};

export default Header;